/**
 * Portal Billing Page
 * Faturação — receita bruta, comissões, valor líquido e liquidações
 */

import React, { useEffect, useState } from 'react';
import api from '../../services/api';

const fmt = (val?: number | null) =>
  Number(val || 0).toLocaleString('pt-PT', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const formatDate = (val: any): string => {
  if (!val) return '—';
  try {
    const d = new Date(String(val).split('T')[0] + 'T12:00:00Z');
    return isNaN(d.getTime()) ? String(val) : d.toLocaleDateString('pt-CV');
  } catch {
    return String(val);
  }
};

interface BillingStats {
  receitaBruta: number;
  comissaoPagali: number;
  valorLiquido: number;
  totalTransferido: number;
  pendenteTransferencia: number;
  taxaComissao: number;
}

interface Transacao {
  id: string;
  referencia: string;
  descricao: string;
  tipo: string;
  data: string;
  valor: number;
  comissao: number;
  valorLiquido: number;
  estado: string;
}

interface Liquidacao {
  id: string;
  referencia: string;
  valor: number;
  data: string;
  estado: string;
}

const PortalBilling: React.FC = () => {
  const [stats, setStats] = useState<BillingStats | null>(null);
  const [transacoes, setTransacoes] = useState<Transacao[]>([]);
  const [liquidacoes, setLiquidacoes] = useState<Liquidacao[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dias, setDias] = useState(30);
  const [activeTab, setActiveTab] = useState<'transacoes' | 'liquidacoes'>('transacoes');

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get(`/portal/billing/summary?dias=${dias}`);
      const d = res.data?.data;
      if (d) {
        setStats(d.stats || null);
        setTransacoes(d.transacoes || []);
        setLiquidacoes(d.liquidacoes || []);
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || 'Erro ao carregar faturação');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [dias]);

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pagali-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Faturação</h1>
          <p className="text-gray-600 text-sm mt-0.5">
            Receita, comissões e liquidações da sua entidade
          </p>
        </div>
        <select
          value={dias}
          onChange={(e) => setDias(Number(e.target.value))}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pagali-primary"
        >
          <option value={7}>Últimos 7 dias</option>
          <option value={30}>Últimos 30 dias</option>
          <option value={90}>Últimos 90 dias</option>
        </select>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600 text-sm">{error}</p>
          <button
            onClick={() => setError(null)}
            className="text-xs text-red-500 underline mt-1"
          >
            Fechar
          </button>
        </div>
      )}

      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Receita Bruta */}
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Receita Bruta</p>
          <p className="text-2xl font-bold text-green-600 mt-1">
            {fmt(stats?.receitaBruta)} <span className="text-sm font-normal text-gray-400">ECV</span>
          </p>
          <p className="text-xs text-gray-400 mt-1">Total recebido no período</p>
        </div>

        {/* Comissão Pagali */}
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Comissão Pagali</p>
          <p className="text-2xl font-bold text-orange-500 mt-1">
            {fmt(stats?.comissaoPagali)} <span className="text-sm font-normal text-gray-400">ECV</span>
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Taxa: {stats?.taxaComissao ?? 5}%
          </p>
        </div>

        {/* Valor Líquido */}
        <div className="bg-gradient-to-br from-violet-50 to-indigo-50 border border-violet-200 rounded-lg shadow p-6">
          <p className="text-xs font-medium text-violet-600 uppercase tracking-wide">Valor Líquido</p>
          <p className="text-2xl font-bold text-violet-700 mt-1">
            {fmt(stats?.valorLiquido)} <span className="text-sm font-normal text-violet-400">ECV</span>
          </p>
          <p className="text-xs text-violet-500 mt-1">Após dedução de comissão</p>
        </div>

        {/* Pendente de Transferência */}
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Pendente Transferência</p>
          <p className="text-2xl font-bold text-yellow-600 mt-1">
            {fmt(stats?.pendenteTransferencia)} <span className="text-sm font-normal text-gray-400">ECV</span>
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Transferido: {fmt(stats?.totalTransferido)} ECV
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-8">
          <button
            onClick={() => setActiveTab('transacoes')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'transacoes'
                ? 'border-pagali-primary text-pagali-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Transacções
            {transacoes.length > 0 && (
              <span className="ml-2 bg-gray-100 text-gray-600 text-xs rounded-full px-2 py-0.5">
                {transacoes.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('liquidacoes')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'liquidacoes'
                ? 'border-pagali-primary text-pagali-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Liquidações
            {liquidacoes.length > 0 && (
              <span className="ml-2 bg-gray-100 text-gray-600 text-xs rounded-full px-2 py-0.5">
                {liquidacoes.length}
              </span>
            )}
          </button>
        </nav>
      </div>

      {/* Transacções Tab */}
      {activeTab === 'transacoes' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pagali-primary"></div>
            </div>
          ) : transacoes.length === 0 ? (
            <div className="px-6 py-12 text-center text-gray-500 text-sm">
              Nenhum registo encontrado.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Descrição
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tipo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Valor
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Comissão
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Valor Líquido
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estado
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {transacoes.map((t) => (
                    <tr key={t.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <p className="text-sm font-medium text-gray-900">{t.descricao}</p>
                        {t.referencia && (
                          <p className="text-xs text-gray-400 mt-0.5">{t.referencia}</p>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            t.tipo === 'DONATIVO'
                              ? 'bg-pink-100 text-pink-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}
                        >
                          {t.tipo}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {formatDate(t.data)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                        {fmt(t.valor)} ECV
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-orange-600 text-right">
                        {fmt(t.comissao)} ECV
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-violet-700 text-right">
                        {fmt(t.valorLiquido)} ECV
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          {t.estado}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Liquidações Tab */}
      {activeTab === 'liquidacoes' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pagali-primary"></div>
            </div>
          ) : liquidacoes.length === 0 ? (
            <div className="px-6 py-12 text-center text-gray-500 text-sm">
              Nenhum registo encontrado.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Referência
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Valor
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estado
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {liquidacoes.map((l) => (
                    <tr key={l.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {l.referencia || '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {formatDate(l.data)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 text-right">
                        {fmt(l.valor)} ECV
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            l.estado === 'CONCLUIDA'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {l.estado}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PortalBilling;
